import { AuthService } from '../service/auth.service';
import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpClient,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, of } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from 'environments/environment';

const TOKEN_EXPIRED_KEYWORDS = [
  'token is expired',
  'token invalide',
  'non connecté',
  'unauthorized',
];

/** Réponses du serveur qui signifient que le jeton de renouvellement n'est plus valable. */
const STATUTS_REFRESH_REFUSE = [400, 401, 403];

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshDone$ = new BehaviorSubject<string | null>(null);
  private sessionExpiredShown = false;

  constructor(
    private authenticationService: AuthService,
    private router: Router,
    private http: HttpClient
  ) {}

  private handleSessionExpired() {
    if (this.sessionExpiredShown) return;
    this.sessionExpiredShown = true;
    this.isRefreshing = false;
    this.authenticationService.logout();
    this.router.navigate(['/authentication/signin']).then(() => {
      this.sessionExpiredShown = false;
    });
  }

  /**
   * Renouvelle le jeton puis rejoue la requête refusée.
   * `echec` est rendu à l'appelant si le renouvellement est impossible pour une raison
   * passagère (réseau, serveur indisponible) : la session est conservée.
   */
  private refreshToken(
    failedRequest: HttpRequest<any>,
    next: HttpHandler,
    echec: () => Observable<HttpEvent<any>>
  ): Observable<HttpEvent<any>> {
    // Un autre onglet a pu renouveler le jeton entre-temps : il suffit de rejouer avec le nouveau.
    const jetonCourant = this.authenticationService.getAccessToken();
    const jetonUtilise = failedRequest.headers.get('Authorization')?.replace('Bearer ', '');
    if (jetonCourant && jetonUtilise && jetonCourant !== jetonUtilise) {
      return next.handle(this.addToken(failedRequest, jetonCourant));
    }

    const refreshToken = this.authenticationService.getRefreshToken();
    if (!refreshToken) {
      this.handleSessionExpired();
      return echec();
    }

    if (this.isRefreshing) {
      return this.refreshDone$.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((newToken) => next.handle(this.addToken(failedRequest, newToken!)))
      );
    }

    this.isRefreshing = true;
    this.refreshDone$.next(null);

    return this.http.post<any>(`${environment.apiUrl}auth/token/refresh`, { refresh: refreshToken }).pipe(
      switchMap((res: any) => {
        this.isRefreshing = false;
        const newAccess: string = res.access;
        const newRefresh: string = res.refresh ?? refreshToken;
        this.authenticationService.updateAccessToken(newAccess, newRefresh);
        this.refreshDone$.next(newAccess);
        return next.handle(this.addToken(failedRequest, newAccess));
      }),
      catchError((err: HttpErrorResponse) => {
        this.isRefreshing = false;
        // Seul un refus explicite du serveur met fin à la session ; une panne passagère non.
        if (STATUTS_REFRESH_REFUSE.includes(err?.status)) {
          // Un autre onglet a pu consommer ce jeton de renouvellement juste avant nous.
          const rafraichiAilleurs = this.authenticationService.getRefreshToken();
          if (rafraichiAilleurs && rafraichiAilleurs !== refreshToken) {
            return next.handle(this.addToken(failedRequest, this.authenticationService.getAccessToken()!));
          }
          this.handleSessionExpired();
        }
        return echec();
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Ne pas intercepter l'endpoint de refresh (éviter boucle infinie)
    if (request.url.includes('auth/token/refresh')) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      switchMap((event) => {
        // L'API signale un jeton expiré dans une réponse 200 (hasError) : la requête est rejouée
        // après renouvellement et c'est sa réponse qui est rendue à l'écran.
        if (event instanceof HttpResponse && event.body?.hasError) {
          const msg: string = (event.body?.status?.message || '').toLowerCase();
          if (TOKEN_EXPIRED_KEYWORDS.some((kw) => msg.includes(kw))) {
            return this.refreshToken(request, next, () => of(event));
          }
        }
        return of(event);
      }),
      catchError((err) => {
        if (err.status === 401) {
          return this.refreshToken(request, next, () => throwError(() => err?.error?.message || err.statusText));
        }
        const error = err?.error?.message || err.statusText;
        return throwError(() => error);
      })
    );
  }
}
